// ============================================================
// Avatar.tsx
// Shared user-avatar circle — initials on a solid colour bg.
// Used by the AppHeader (size="sm") for the always-visible
// top-right avatar, and by the Settings profile block
// (size="lg") for the larger user-identity tile next to the
// name + email.
//
// Colour is a hex string sourced from authUser.avatarColour
// (the user's pick from PRESET_AVATAR_COLOURS, persisted via
// PATCH /api/auth/me). When undefined / empty, falls back to
// the brand green — so existing accounts without a saved
// choice still show a sensible Casevo-identity avatar.
//
// Background is applied via inline style because Tailwind
// can't generate dynamic class names from a runtime string.
// Text is white in both themes; the solid bg + white text
// pattern works across light AND dark page surfaces without
// per-mode treatment.
// ============================================================
import React from 'react'

// Default colour used when authUser.avatarColour is missing /
// empty. Matches PRESET_AVATAR_COLOURS[0] so the Settings
// picker highlights the default swatch for users who haven't
// chosen yet.
export const DEFAULT_AVATAR_COLOUR = '#2d6a4f'

// Curated preset palette. All AA-contrast safe against white
// text + work as solid backgrounds against both light
// (#f2f8f5) and dark (#19211d) page bgs. Mostly Tailwind's
// `-700` tier — designed for "solid bg + white text" pairs.
// The picker in Settings → Appearance shows these swatches
// in declared order; the user clicks one to save.
export const PRESET_AVATAR_COLOURS: readonly string[] = [
  '#2d6a4f', // Casevo Brand
  '#334155', // Slate
  '#57534e', // Stone
  '#0f766e', // Teal
  '#0369a1', // Sky
  '#4338ca', // Indigo
  '#be123c', // Rose
  '#b45309', // Amber
] as const

interface AvatarProps {
  firstName: string
  lastName: string
  /** Falls back to first char of email when both name fields
   *  are empty (e.g. brand-new account with email only). */
  email?: string
  /** Hex string from authUser.avatarColour. Defaults to brand
   *  green when undefined / empty. */
  colour?: string
  /** xs = 20px (inline indicators), sm = 32px (header), md = 40px,
   *  lg = 56px (Settings). Default md so a bare <Avatar firstName ... />
   *  still renders sensibly. */
  size?: 'xs' | 'feed' | 'pill' | 'rail' | 'sm' | 'md' | 'nav' | 'lg' | 'xl'
  /** Rendering style (from authUser.avatarStyle). Default 'solid' = the
   *  original look, so existing avatars are unchanged.
   *    solid — solid colour fill, white initial.
   *    soft  — low-alpha colour tint, initial in full colour.
   *    icon  — person glyph (no initial) in colour on the soft tint. */
  style?: 'solid' | 'soft' | 'icon'
  /** Presigned URL of the user's uploaded avatar photo (from
   *  authUser.avatarPhotoUrl). When set, the photo is shown and the
   *  style/colour initial is IGNORED (photo takes precedence); when
   *  null/undefined, the styled initial is the fallback. */
  photoUrl?: string | null
}

// Compute a SINGLE initial: the first character of firstName, falling
// back to the first character of email for a brand-new account with no
// name yet. Uppercased via the `uppercase` class on the circle.
const computeInitial = (firstName: string, email?: string): string => {
  const f = firstName?.charAt(0) ?? ''
  if (f.length > 0) return f
  return email?.charAt(0) ?? '?'
}

// Convert a #rrggbb (or #rgb) hex to an rgba() string at the given alpha
// — used by the 'soft' + 'icon' styles for a low-opacity tinted fill.
// Falls back to the raw input on a malformed hex so we never crash.
const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '')
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return hex
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Tailwind size mappings. text-* paired with w/h-* gives the
// initials a consistent ratio inside the circle across sizes.
const SIZE_CLASSES: Record<NonNullable<AvatarProps['size']>, string> = {
  // xs = 20px — for inline indicators (e.g. the field-presence dot next
  // to a form label). Added for Phase 2a; existing callers default to md.
  xs: 'w-5 h-5 text-[10px]',
  // feed = 22px — Activity timeline who/when line.
  feed: 'w-[22px] h-[22px] text-[10.5px]',
  // pill = 24px — Admin → Permissions member-picker pills.
  pill: 'w-6 h-6 text-[10.5px]',
  // rail = 26px — Client Notes pinned-rail author line (mockup .av.sm).
  rail: 'w-[26px] h-[26px] text-[11px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  // nav = 38px / xl = 64px — the Settings section-nav "who" block and the
  // Settings identity card (Option B redesign spec values).
  nav: 'w-[38px] h-[38px] text-[14px]',
  lg: 'w-14 h-14 text-lg',
  xl: 'w-16 h-16 text-[22px]',
}

const Avatar: React.FC<AvatarProps> = ({
  firstName,
  lastName,
  email,
  colour,
  size = 'md',
  style = 'solid',
  photoUrl,
}) => {
  const initial = computeInitial(firstName, email)
  const colourValue = colour && colour.length > 0 ? colour : DEFAULT_AVATAR_COLOUR
  const ariaLabel = `${firstName} ${lastName}`.trim() || email || 'User avatar'
  const shapeClass = `${SIZE_CLASSES[size]} rounded-full flex items-center justify-center shrink-0`

  // PHOTO — takes precedence over every styled-initial branch. When a
  // photo is set it's the avatar; the styled initial below is only the
  // fallback for when no photo exists. object-cover keeps the (already
  // square-cropped) image filling the circle at any size.
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={ariaLabel}
        className={`${SIZE_CLASSES[size]} rounded-full object-cover shrink-0`}
      />
    )
  }

  // ICON — a person glyph instead of the initial, coloured, on a soft
  // tint of the same colour. Glyph is 50% of the circle so it scales
  // across sm/md/lg.
  if (style === 'icon') {
    return (
      <div
        className={shapeClass}
        style={{ backgroundColor: hexToRgba(colourValue, 0.16) }}
        aria-label={ariaLabel}
      >
        <svg
          className="w-1/2 h-1/2"
          viewBox="0 0 24 24"
          fill="none"
          stroke={colourValue}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.5-6 8-6s8 2 8 6" />
        </svg>
      </div>
    )
  }

  // SOFT — low-alpha tint fill, initial rendered in the FULL colour.
  if (style === 'soft') {
    return (
      <div
        className={`${shapeClass} font-medium uppercase`}
        style={{ backgroundColor: hexToRgba(colourValue, 0.16), color: colourValue }}
        aria-label={ariaLabel}
      >
        {initial}
      </div>
    )
  }

  // SOLID (default) — original look: solid colour fill, white initial.
  return (
    <div
      className={`${shapeClass} text-white font-medium uppercase`}
      style={{ backgroundColor: colourValue }}
      aria-label={ariaLabel}
    >
      {initial}
    </div>
  )
}

export default Avatar
